import { useEffect, useRef } from 'react';

import { composeDictation } from '../utils/composeDictation';

import type { DictationController } from './useDictation';

/**
 * Folds a dictation session into a controlled string value. The value at the
 * moment dictation starts is the base; every turn update re-emits
 * base + spoken text, and the end of the session commits it.
 */
export function useDictatedValue({
  value,
  onValueChange,
  controller,
  maxLength,
}: {
  value: string;
  onValueChange: (value: string) => void;
  controller: DictationController;
  /** The field's character cap; spoken text is cut to fit, as typing would be. */
  maxLength?: number;
}): void {
  const base = useRef<string | null>(null);
  /** The last value this hook wrote; anything else arriving is an outside edit. */
  const emitted = useRef<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const emit = useRef(onValueChange);
  emit.current = onValueChange;
  const { status, turns, consumeTurns, stop } = controller;

  useEffect(() => {
    if (status === 'starting') {
      base.current = valueRef.current;
      emitted.current = valueRef.current;
    }
  }, [status]);

  useEffect(() => {
    if (base.current === null || turns.length === 0) return;
    const composed = composeDictation(base.current, turns);
    const next = maxLength !== undefined ? composed.slice(0, maxLength) : composed;
    if (next !== valueRef.current) {
      emitted.current = next;
      emit.current(next);
    }
  }, [turns, maxLength]);

  // The host changed the value underneath a live session (a composer cleared
  // it on send, an undo). The base is stale, so end the session rather than
  // re-composing sent text into the field.
  useEffect(() => {
    if (base.current === null || emitted.current === null) return;
    if (value !== emitted.current) {
      // Drop the base now, not on `idle`: a final turn flushed while the
      // engine closes must not be composed back into the field.
      base.current = null;
      emitted.current = null;
      consumeTurns();
      stop();
    }
  }, [value, stop, consumeTurns]);

  useEffect(() => {
    if (status !== 'idle' || base.current === null) return;
    base.current = null;
    emitted.current = null;
    consumeTurns();
  }, [status, consumeTurns]);
}
